import { FC, useState } from 'react';

import { resendReselectNotice } from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponForRedemptionDashboard } from '@shared/api/giftCoupon/giftCoupon.interface';
import { giftCouponReselectRecipientUrl } from '@shared/utils/giftRecipientLink.util';
import { Link } from 'react-router-dom';

import { CsMemoCouponCell } from './csMemo';

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${m}.${day} ${h}:${min}`;
};

const fmtDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getMonth() + 1}.${d.getDate()}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '-';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

/** 품절된(직전 선택) 상품명 — reselect 기록 우선, 없으면 현재 선택 상품 */
const soldOutLabel = (c: GiftCouponForRedemptionDashboard): string => {
  const itemName =
    typeof c.giftItem === 'object'
      ? c.giftItem?.name || c.giftItem?.giftItemInfo?.name || '-'
      : '-';
  const soldOut = c.reselect?.soldOutChoiceUnit;
  const unitName =
    soldOut && typeof soldOut === 'object'
      ? soldOut.name
      : c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object'
      ? c.selectedChoiceUnit.name
      : '';
  return unitName ? `${itemName} - ${unitName}` : itemName;
};

const ordererOf = (c: GiftCouponForRedemptionDashboard) => {
  const order = typeof c.giftOrder === 'object' ? c.giftOrder : null;
  return order?.bugo?.member ?? null;
};

interface ReselectTabProps {
  items: GiftCouponForRedemptionDashboard[];
  /**
   * pending: 안내 후 수령자 응답 대기 / cancelRequested: 수령자가 선물 거절(구매자 환불) 요청
   */
  mode: 'pending' | 'cancelRequested';
  /** 안내 철회 (발송대기로 복귀) — Admin·GiftAgent 모두 가능 */
  onCancelNotice: (couponId: string) => Promise<boolean>;
  /** 문자 재발송 노출 여부 — 서버가 Admin 한정이라 GiftAgent에게는 숨긴다 */
  canResend: boolean;
  /** 선물거절 건의 구매자 취소 처리 — 주문 쿠폰 상세 모달을 그 쿠폰이 선택된 채로 연다 */
  onOpenOrderCancel?: (orderId: string, couponId: string) => void;
  copyToClipboard: (text: string) => void;
  showToast: (message: string) => void;
}

/**
 * 품절 재선택 안내 / 취소(선물거절) 탭.
 * 스펙: docs/gift-coupon-reselect-soldout-spec.md
 * - 취소는 주문 쿠폰 상세 모달을 재사용해 **구매자 결제 취소**로 바로 마감한다.
 *   미공유 처리로 되돌리는 우회는 백엔드 가드가 풀려 더 이상 필요 없다(§2-6-1).
 * - 기한이 지난 무응답 건(`Notified`)도 같은 방식으로 취소할 수 있다.
 */
export const ReselectTab: FC<ReselectTabProps> = ({
  items,
  mode,
  onCancelNotice,
  canResend,
  onOpenOrderCancel,
  copyToClipboard,
  showToast,
}) => {
  const [busyId, setBusyId] = useState<string | null>(null);

  const handleResend = async (couponId: string) => {
    // 재발송은 안내 시각·기한·안내 횟수를 갱신하지 않는다(서버 동작) — 문자만 다시 나간다
    if (
      !confirm(
        '재선택 안내 문자를 다시 보내시겠습니까?\n(안내 시각과 재선택 기한은 그대로 유지됩니다)',
      )
    ) {
      return;
    }
    setBusyId(couponId);
    try {
      const res = await resendReselectNotice(couponId, {
        baseUrl: window.location.origin,
      });
      if (res.sent) {
        showToast('안내 문자를 다시 보냈습니다.');
      } else {
        alert(`발송에 실패했습니다.${res.reason ? `\n(${res.reason})` : ''}`);
      }
    } catch (error) {
      const statusCode = (error as { response?: { status?: number } })?.response?.status;
      alert(
        statusCode === 404
          ? '서버에 아직 반영되지 않은 기능입니다.'
          : statusCode === 403
          ? '재발송은 관리자만 할 수 있습니다.'
          : '재발송에 실패했습니다.',
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (couponId: string) => {
    setBusyId(couponId);
    try {
      await onCancelNotice(couponId);
    } finally {
      setBusyId(null);
    }
  };

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white py-12 text-center text-sm text-gray-400">
        {mode === 'pending'
          ? '재선택 안내 중인 건이 없습니다.'
          : '접수된 취소요청이 없습니다.'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {mode === 'pending' && (
        <p className="rounded-lg bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-800">
          품절 안내를 보내고 수령자의 재선택을 기다리는 건입니다. 수령자가 다른 상품을
          고르면 자동으로 발송대기로 돌아갑니다.
          <br />
          <span className="font-bold">기한이 지난 건</span>은 고지한 대로 구매자 전액
          환불로 마감해 주세요 — 해당 행의 <span className="font-bold">취소 처리</span>
          에서 바로 진행할 수 있습니다.
        </p>
      )}

      {mode === 'cancelRequested' && (
        <p className="rounded-lg bg-rose-50 px-3.5 py-3 text-sm leading-relaxed text-rose-800">
          수령자가 <span className="font-bold">선물을 받지 않겠다</span>고 요청한
          건입니다.
          <span className="font-bold"> 취소 처리</span>를 누르면 해당 주문의 쿠폰 상세가
          열리고, 그 쿠폰만 선택된 상태로{' '}
          <span className="font-bold">구매자 결제 취소</span>를 진행할 수 있습니다.
          (가상계좌·입금 결제는 환불이체 모달로 이어집니다)
          <br />
          <span className="font-bold">구매자 안내 문자는 취소 처리 시 서버가 자동으로
          보냅니다</span> — 취소 금액과 함께 &ldquo;수령인이 받지 않아 취소&rdquo;라는 사유가
          쿠폰번호로 표기됩니다. 같은 주문은{' '}
          <Link to="/gift-seller/search?tab=order" className="font-bold underline">
            주문 검색
          </Link>
          에서도 다시 열 수 있습니다.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="whitespace-nowrap px-4 py-2 font-medium">품절 상품</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">코드</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">수령자</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">주문자(상주)</th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">
                {mode === 'pending' ? '안내일시 / 기한' : '요청일시'}
              </th>
              <th
                className="whitespace-nowrap px-4 py-2 font-medium"
                title="CS 응대 메모 (클릭 시 메모장)"
              >
                CS메모
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium">처리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((c) => {
              const orderer = ordererOf(c);
              const deadline = c.reselect?.deadlineAt
                ? new Date(c.reselect.deadlineAt)
                : null;
              const isOverdue = !!deadline && deadline.getTime() < Date.now();
              return (
                <tr key={c._id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-800">
                    <span className="line-through decoration-gray-300">
                      {soldOutLabel(c)}
                    </span>
                    {(c.reselect?.noticeCount ?? 0) > 1 && (
                      <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-500">
                        {c.reselect?.noticeCount}회 안내
                      </span>
                    )}
                    {c.reselect?.reason && (
                      <p className="mt-0.5 text-xs text-gray-400">{c.reselect.reason}</p>
                    )}
                    {mode === 'cancelRequested' && c.reselect?.cancelReason && (
                      <p className="mt-0.5 text-xs text-rose-600">
                        “{c.reselect.cancelReason}”
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-gray-600">
                    {c.fullCode || c.code}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                    <button
                      onClick={() =>
                        copyToClipboard(
                          (c.recipient?.phoneNumber || '').replace(/[^0-9]/g, ''),
                        )
                      }
                      className="hover:underline"
                      title="번호 복사"
                    >
                      {fmtPhone(c.recipient?.phoneNumber)}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                    {orderer ? (
                      <button
                        onClick={() =>
                          copyToClipboard(
                            (orderer.phoneNumber || '').replace(/[^0-9]/g, ''),
                          )
                        }
                        className="text-left hover:underline"
                        title="주문자 번호 복사 (주문 검색에 붙여넣기)"
                      >
                        {orderer.fullName}
                        <span className="ml-1 text-gray-400">
                          {fmtPhone(orderer.phoneNumber)}
                        </span>
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                    {mode === 'pending' ? (
                      <>
                        {fmtTime(c.reselect?.noticeSentAt)}
                        {deadline && (
                          <span
                            className={`ml-1.5 rounded px-1.5 py-0.5 text-[11px] font-bold ${
                              isOverdue
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {isOverdue ? '기한 경과' : `~${fmtDate(deadline)}`}
                          </span>
                        )}
                      </>
                    ) : (
                      fmtTime(c.reselect?.cancelRequestedAt)
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <CsMemoCouponCell
                      coupon={c}
                      subtitle={
                        orderer
                          ? `${orderer.fullName} · ${fmtPhone(orderer.phoneNumber)}`
                          : undefined
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <div className="flex gap-2">
                      {onOpenOrderCancel && (mode === 'cancelRequested' || isOverdue) && (
                        <button
                          onClick={() => {
                            const orderId =
                              typeof c.giftOrder === 'object' ? c.giftOrder?._id : '';
                            if (!orderId) {
                              alert('주문 정보를 찾을 수 없습니다.');
                              return;
                            }
                            onOpenOrderCancel(orderId, c._id);
                          }}
                          className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                            mode === 'cancelRequested'
                              ? 'bg-rose-600 text-white hover:bg-rose-700'
                              : 'border border-rose-300 text-rose-700 hover:bg-rose-50'
                          }`}
                        >
                          취소 처리
                        </button>
                      )}
                      {mode === 'pending' && canResend && (
                        <button
                          onClick={() => handleResend(c._id)}
                          disabled={busyId === c._id}
                          className="rounded-lg border border-amber-400 px-3 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                        >
                          문자 재발송
                        </button>
                      )}
                      <button
                        onClick={() =>
                          copyToClipboard(giftCouponReselectRecipientUrl(c._id))
                        }
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                        title="수령자 재선택 링크 복사"
                      >
                        링크 복사
                      </button>
                      <button
                        onClick={() => handleCancel(c._id)}
                        disabled={busyId === c._id}
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        title="발송대기로 되돌립니다"
                      >
                        안내 취소
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
